import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { fmtDate } from "@/components/DatePicker";
import { ShieldAlert, ShieldCheck, ChevronRight } from "lucide-react";

const SEV = { high: "bg-rose-100 text-rose-700 border-rose-200", medium: "bg-amber-100 text-amber-700 border-amber-200", low: "bg-slate-100 text-slate-600 border-slate-200" };

export function AnomalyAlerts({ runId, compact = false }) {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/payroll/anomalies", { params: runId ? { run_id: runId } : {} }).then((r) => setD(r.data)).catch(() => {}), [runId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  if (d.count === 0) {
    if (compact) return <div className="flex items-center gap-2 text-xs text-emerald-700 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="anomaly-clear"><ShieldCheck className="w-4 h-4" /> No anomalies detected in this run.</div>;
    if (d.runs_checked === 0) return null;
    return <Card className="p-4 flex items-center gap-3 text-sm" data-testid="anomaly-clear"><ShieldCheck className="w-5 h-5 text-emerald-600" /> {d.runs_checked} payroll run{d.runs_checked === 1 ? "" : "s"} awaiting approval — no anomalies detected.</Card>;
  }
  const list = d.alerts.slice(0, compact ? 50 : 8);
  const Wrap = compact ? "div" : Card;
  return (
    <Wrap className={compact ? "rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-1.5" : "p-4 space-y-2"} data-testid="anomaly-alerts">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-sm flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-600" /> {compact ? "Review before approving" : "Payroll anomaly alerts"} <span className="text-xs font-normal text-muted-foreground">{d.count} flag{d.count === 1 ? "" : "s"}{d.high ? ` · ${d.high} high` : ""}{!compact && ` · ${d.runs_checked} run${d.runs_checked === 1 ? "" : "s"} pending approval`}</span></div>
      </div>
      <div className={compact ? "space-y-1 max-h-40 overflow-y-auto" : "divide-y"}>
        {list.map((a, i) => (
          <div key={i} data-testid={`anomaly-${a.type}`} className={`flex items-start gap-2 text-sm ${compact ? "" : "py-1.5"}`}>
            <span className={`shrink-0 mt-0.5 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border ${SEV[a.severity]}`}>{a.severity}</span>
            <span className="flex-1">{a.message}{!compact && <span className="text-xs text-muted-foreground"> · {a.company_name} · pay {fmtDate(a.pay_date)}</span>}</span>
            {!compact && <button data-testid={`anomaly-open-${i}`} onClick={() => navigate(`/payroll?run=${a.run_id}`)} className="text-xs text-primary hover:underline flex items-center shrink-0">Review <ChevronRight className="w-3 h-3" /></button>}
          </div>))}
        {!compact && d.count > 8 && <div className="text-xs text-muted-foreground pt-1">+{d.count - 8} more — open the run to see all flags.</div>}
      </div>
    </Wrap>
  );
}
